import {
    TITLE_MAX_LENGTH,
    useShortcutFormSetup,
} from "@/hse/features/Dashboard/Shortcuts/ShortcutForm/formHooks";
import type { TaskShortcut } from "@dashboard/api/task";

import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "frontend-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type ShortcutModalProps =
    | {
          mode: "create";
          trigger: React.ReactElement;
      }
    | {
          mode: "edit";
          shortcut: TaskShortcut;
          trigger?: React.ReactElement;
          open?: boolean;
          onOpenChange?: (open: boolean) => void;
      };

export function ShortcutModal(props: ShortcutModalProps) {
    const { t } = useTranslation();
    const [internalOpen, setInternalOpen] = useState(false);
    const { useShortcutForm, formOpts, mapShortcutToForm, handleShortcutSubmit } =
        useShortcutFormSetup();
    const [submitError, setSubmitError] = useState<string | null>(null);

    const open = props.mode === "edit" && props.open !== undefined ? props.open : internalOpen;
    const setOpen = (v: boolean) => {
        if (props.mode === "edit" && props.onOpenChange) {
            props.onOpenChange(v);
        } else {
            setInternalOpen(v);
        }
    };

    const shortcutId = props.mode === "edit" ? props.shortcut.id : undefined;

    const form = useShortcutForm({
        defaultValues:
            props.mode === "edit" ? mapShortcutToForm(props.shortcut) : formOpts.defaultValues,
        validators: formOpts.validators,
        onSubmit: async ({ value }) => {
            setSubmitError(null);

            const result = await handleShortcutSubmit(value, shortcutId);
            if (!result.success) {
                setSubmitError(t("dashboard.shortcuts.errorSaving"));
                return;
            }
            form.reset();
            setOpen(false);
        },
    });

    const headingKey =
        props.mode === "edit"
            ? "dashboard.shortcuts.editShortcut"
            : "dashboard.shortcuts.addShortcut";
    const submitLabelKey = props.mode === "edit" ? "common.save" : "common.add";

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (form.state.isSubmitting) return;
                setOpen(v);
                if (!v) {
                    form.reset();
                    setSubmitError(null);
                }
            }}
        >
            {props.trigger && <DialogTrigger render={props.trigger} />}
            <DialogContent>
                <DialogHeader className="-mx-6 pl-6 pr-24 pb-4 border-b border-compendia-border">
                    <DialogTitle className="text-lg font-bold text-compendia-foreground leading-tight overflow-hidden text-ellipsis whitespace-nowrap">
                        {t(headingKey)}
                    </DialogTitle>
                </DialogHeader>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void form.handleSubmit();
                    }}
                    className="flex flex-col gap-4"
                >
                    <form.AppField
                        name="title"
                        children={(field) => (
                            <field.Input
                                label={t("common.title")}
                                placeholder={t("dashboard.shortcuts.titlePlaceholder")}
                                maxLength={TITLE_MAX_LENGTH}
                                showCharCount={true}
                            />
                        )}
                    />

                    <form.AppField
                        name="url"
                        children={(field) => (
                            <field.Input label={t("common.url")} placeholder="https://..." />
                        )}
                    />

                    <form.AppField
                        name="icon"
                        children={(field) => (
                            <field.IconSelect label={t("dashboard.shortcuts.icon")} />
                        )}
                    />

                    {submitError && (
                        <p role="alert" className="text-chameleon-pink text-sm">
                            {submitError}
                        </p>
                    )}

                    <DialogFooter className="flex-row justify-end">
                        <form.Subscribe selector={(state) => [state.isSubmitting, state.canSubmit]}>
                            {([isSubmitting, canSubmit]) => (
                                <>
                                    <DialogClose
                                        render={
                                            <Button
                                                type="button"
                                                variant="outline"
                                                disabled={isSubmitting}
                                            >
                                                {t("common.cancel")}
                                            </Button>
                                        }
                                    />
                                    <Button
                                        type="submit"
                                        variant="submit"
                                        size="submit"
                                        disabled={!canSubmit}
                                    >
                                        {t(submitLabelKey)}
                                    </Button>
                                </>
                            )}
                        </form.Subscribe>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
